import React from 'react';
import { FileText } from 'lucide-react';

interface PersonalDetailTabProps {
  employeeData: any;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const formatGender = (gender: string | null | undefined) => {
  if (!gender) return '-';
  return gender === 'male' ? 'Laki-laki' : gender === 'female' ? 'Perempuan' : gender;
};

const formatMaritalStatus = (status: string | null | undefined) => {
  if (!status) return '-';
  const statusMap: Record<string, string> = {
    single: 'Belum Menikah',
    married: 'Menikah',
    divorced: 'Cerai',
    widowed: 'Janda/Duda',
  };
  return statusMap[status] || status;
};

const isImageUrl = (url: string | null | undefined) => {
  if (!url) return false;
  return url.match(/\.(jpg|jpeg|png|gif|webp)$/i) !== null;
};

const isPdfUrl = (url: string | null | undefined) => {
  if (!url) return false;
  return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf');
};

export default function PersonalDetailTab({ employeeData }: PersonalDetailTabProps) {
  const ktpUrl = employeeData.ktp_file_url || employeeData.ktp_url;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Pribadi</h3>
        <p className="text-gray-500 text-sm">Informasi identitas dan kontak pegawai</p>
      </div>

      <div className="flex items-center gap-6">
        {employeeData.photo_url ? (
          <img
            src={employeeData.photo_url}
            alt={employeeData.full_name || 'Foto Pegawai'}
            className="w-24 h-24 rounded-full object-cover border-2 border-emerald-200"
          />
        ) : (
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 text-2xl font-bold">
            {employeeData.full_name?.charAt(0) || 'U'}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.full_name || '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">NIK</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 font-mono">
            {employeeData.nik && employeeData.nik.trim() !== '' ? employeeData.nik : '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tempat Lahir</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.birth_place || '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Lahir</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatDate(employeeData.birth_date)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Kelamin</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatGender(employeeData.gender)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status Pernikahan</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {formatMaritalStatus(employeeData.marital_status)}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. Telepon / WA</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.phone || '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Pribadi</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.email || '-'}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alamat Domisili</label>
        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 min-h-[80px]">
          {employeeData.address || '-'}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nama Kontak Darurat
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.emergency_contact_name || '-'}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. Kontak Darurat</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {employeeData.emergency_contact_phone || '-'}
          </div>
        </div>
      </div>

      {ktpUrl && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">KTP</label>
          <div className="border-2 border-gray-200 rounded-md p-4 bg-gray-50">
            {isImageUrl(ktpUrl) ? (
              <div className="space-y-2">
                <img
                  src={ktpUrl}
                  alt="KTP"
                  className="w-full h-auto max-h-96 object-contain rounded-md"
                />
                <a
                  href={ktpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-all duration-200 text-sm"
                >
                  <FileText className="w-4 h-4" />
                  Buka Gambar
                </a>
              </div>
            ) : isPdfUrl(ktpUrl) ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-10 h-10 text-red-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">KTP.pdf</p>
                    <p className="text-xs text-gray-500">PDF Document</p>
                  </div>
                </div>
                <a
                  href={ktpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-all duration-200 text-sm"
                >
                  <FileText className="w-4 h-4" />
                  Buka PDF
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <FileText className="w-10 h-10 text-gray-600" />
                <div>
                  <p className="text-sm font-medium text-gray-700">File KTP</p>
                  <a
                    href={ktpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-emerald-600 hover:underline"
                  >
                    Lihat File
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
